import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Business,
  DirectionsCar,
  Route,
  Assessment,
  TrendingUp,
  Notifications,
  PlayArrow,
  TableChart,
  Upload,
  BarChart,
  Storage,
  Api,
  Code,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVRPData } from '../contexts/VRPDataContext';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { apiClient } from '../services/api';
// import { GuidedWorkflowTooltips } from '../components/common/GuidedWorkflowTooltips';

// Real data hooks (no mock data allowed)
const useDashboardMetrics = () => {
  const [metrics, setMetrics] = useState({
    activeProjects: 0,
    fleetVehicles: 0,
    efficiencyRate: 0,
    deliveries: 0,
    costSavings: 0,
    loading: true
  });

  // TODO: Replace with Toolpad navigation
  const projects = [];
  
  // Move hook to top level - never call conditionally
  const vrpData = useVRPData();
  const entities = vrpData.entities;

  useEffect(() => {
    // Use actual data from unified architecture
    const loadMetrics = async () => {
      try {
        setMetrics({
          activeProjects: projects.length,
          fleetVehicles: entities.vehicles.length,
          efficiencyRate: entities.locations.length, // Use locations count
          deliveries: entities.jobs.length,
          costSavings: 0, // Keep as placeholder
          loading: false
        });
      } catch (error) {
        console.error('Failed to load dashboard metrics:', error);
        setMetrics(prev => ({ ...prev, loading: false }));
      }
    };

    loadMetrics();
  }, [projects, entities.vehicles, entities.locations, entities.jobs]);

  return metrics;
};

const useSystemStatus = () => {
  const [status, setStatus] = useState({
    vroomSolver: 'checking',
    database: 'checking', 
    apiServices: 'checking',
    backgroundJobs: 'checking',
    loading: true,
    lastUpdate: null as Date | null,
    responseTime: 0
  });

  useEffect(() => {
    const checkSystemHealth = async () => {
      const startTime = Date.now();
      
      try {
        // Use proper API client health check method instead of direct fetch
        const vroomHealthResult = await apiClient.checkVROOMHealth();
        
        const responseTime = Date.now() - startTime;
        
        // Map health check results to frontend display
        const mapVROOMStatus = (available: boolean) => {
          return available ? 'online' : 'offline';
        };
        
        setStatus({
          vroomSolver: mapVROOMStatus(vroomHealthResult.vroom_available),
          database: 'online', // Assume online if we can make API calls
          apiServices: 'online', // Assume online if we can make API calls 
          backgroundJobs: 'online', // Assume online if API is responding
          loading: false,
          lastUpdate: new Date(),
          responseTime: Math.round(responseTime)
        });
        
      } catch (error) {
        console.error('Failed to check system status:', error);
        
        // If health checks fail, show degraded status
        setStatus({
          vroomSolver: 'offline',
          database: 'warning', // Database might be up even if backend is down
          apiServices: 'offline',
          backgroundJobs: 'offline',
          loading: false,
          lastUpdate: new Date(),
          responseTime: 0
        });
      }
    };

    // Initial check
    checkSystemHealth();
    
    // Set up periodic health checks every 30 seconds
    const interval = setInterval(checkSystemHealth, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return status;
};

const useRecentActivity = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        // Real API call would go here
        setActivities([]);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load recent activity:', error);
        setLoading(false);
      }
    };

    loadActivity();
  }, []);

  return { activities, loading };
};

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const metrics = useDashboardMetrics();
  const systemStatus = useSystemStatus();
  const { activities, loading: activitiesLoading } = useRecentActivity();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleSuggestAction = (action: string, data?: any) => {
    switch (action) {
      case 'create-project':
        navigate('/projects');
        break;
      case 'create-scenario':
        if (currentProject) {
          navigate(`/projects/${currentProject.id}/scenarios`);
        } else {
          navigate('/projects');
        }
        break;
      case 'create-dataset':
        if (currentProject) {
          navigate(`/projects/${currentProject.id}/data-management`);
        } else {
          navigate('/projects');
        }
        break;
      case 'import-data':
        if (currentProject) {
          navigate(`/projects/${currentProject.id}/data-management`);
        } else {
          navigate('/projects');
        }
        break;
      case 'optimisation-compatibility':
        if (currentProject) {
          navigate(`/projects/${currentProject.id}/optimization`);
        } else {
          navigate('/projects');
        }
        break;
      default:
        console.log('Unknown action:', action, data);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': case 'healthy': return '#4caf50';
      case 'warning': case 'slow': case 'degraded': return '#ff9800';
      case 'offline': case 'error': return '#f44336';
      case 'checking': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': case 'healthy': return <CheckCircle />;
      case 'warning': case 'slow': return <Warning />;
      case 'offline': case 'error': return <ErrorIcon />;
      default: return <CheckCircle style={{ opacity: 0.3 }} />;
    }
  };

  const MetricCard = ({ icon, value, label, change, onClick }: any) => (
    <Card 
      onClick={onClick}
      sx={{ 
        minHeight: '90px', // 44% reduction from 160px
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: '#333' }}>
            {metrics.loading ? '—' : value}
          </Typography>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        {change && (
          <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '11px' }}>
            {change}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Generate project-aware URLs
  const getProjectAwareUrl = (basePath: string) => {
    const projectId = currentProject?.id;
    if (projectId && basePath !== '/dashboard' && basePath !== '/projects') {
      return `/projects/${projectId}${basePath}`;
    }
    return basePath;
  };

  return (
    <ProfessionalLayout
      pageTitle="Dashboard"
      pageSubtitle={`Welcome back, ${user?.email?.split('@')[0] || 'User'} - Real-time fleet monitoring and route optimization`}
      activeTab="Dashboard"
      actionButtons={
        <>{/* 
          <GuidedWorkflowTooltips 
            context="projects"
            showProgress={true}
            onSuggestAction={handleSuggestAction}
          /> */}
          <Tooltip title="Refresh data">
            <IconButton color="primary" sx={{ mr: 1 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button 
            variant="outlined" 
            size="small"
            sx={{ mr: 1 }}
          >
            Export Report
          </Button>
          <Button 
            variant="contained" 
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const url = getProjectAwareUrl('/optimization');
              navigate(url === '/optimization' && !currentProject ? '/projects' : url);
            }}
          >
            New Route
          </Button>
        </>
      }
    >

      {/* Compact Metrics Grid - 5 metrics in single row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            icon={<Business sx={{ color: '#1976d2', opacity: 0.7 }} />}
            value={metrics.activeProjects}
            label="Active Projects"
            change={metrics.activeProjects > 0 ? "+0 this week" : ""}
            onClick={() => handleNavigate('/projects')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            icon={<DirectionsCar sx={{ color: '#1976d2', opacity: 0.7 }} />}
            value={metrics.fleetVehicles}
            label="Fleet Vehicles"
            change={metrics.fleetVehicles > 0 ? "+0%" : ""}
            onClick={() => handleNavigate('/vehicles')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            icon={<Route sx={{ color: '#1976d2', opacity: 0.7 }} />}
            value={metrics.efficiencyRate ? `${metrics.efficiencyRate}%` : '0%'}
            label="Efficiency Rate"
            change={metrics.efficiencyRate > 0 ? "+0%" : ""}
            onClick={() => handleNavigate('/optimization-results')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            icon={<Assessment sx={{ color: '#1976d2', opacity: 0.7 }} />}
            value={metrics.deliveries.toLocaleString()}
            label="Deliveries"
            change={metrics.deliveries > 0 ? "+0%" : ""}
            onClick={() => handleNavigate('/jobs')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            icon={<TrendingUp sx={{ color: '#1976d2', opacity: 0.7 }} />}
            value={metrics.costSavings > 0 ? `$${(metrics.costSavings / 1000).toFixed(0)}K` : '$0'}
            label="Cost Savings"
            change={metrics.costSavings > 0 ? "+0%" : ""}
            onClick={() => handleNavigate('/optimization-results')}
          />
        </Grid>
      </Grid>

      {/* Dashboard Content - 2fr/1fr Layout */}
      <Grid container spacing={3}>
        {/* Left Column - 2fr */}
        <Grid item xs={12} lg={8}>
          {/* Quick Actions Panel */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box 
                    onClick={() => handleNavigate('/projects')}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#1976d2', bgcolor: '#f8f9fa' }
                    }}
                  >
                    <Business sx={{ fontSize: 24, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>New Project</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Start planning routes</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    onClick={() => handleNavigate('/optimization')}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#1976d2', bgcolor: '#f8f9fa' }
                    }}
                  >
                    <PlayArrow sx={{ fontSize: 24, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Run Optimization</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Optimize existing routes</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    onClick={() => handleNavigate('/optimization-results')}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#1976d2', bgcolor: '#f8f9fa' }
                    }}
                  >
                    <BarChart sx={{ fontSize: 24, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>View Analytics</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Performance insights</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box 
                    onClick={() => handleNavigate('/data-management')}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1, 
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#1976d2', bgcolor: '#f8f9fa' }
                    }}
                  >
                    <Upload sx={{ fontSize: 24, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Import Data</Typography>
                    <Typography variant="caption" sx={{ color: '#666' }}>Upload CSV files</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* VRP Workflow Guide Panel */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                VRP Workflow Guide
              </Typography>
              
              {!currentProject ? (
                <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                  <Business sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Welcome to VRP System
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Start by creating your first project to organize your vehicle routing optimization work.
                  </Typography>
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/projects')}
                    sx={{ mt: 1 }}
                  >
                    Create First Project
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {metrics.activeProjects}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Active Projects
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        0
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Scenarios
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>Typical VRP Workflow</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1976d2', mr: 1, flexShrink: 0 }} />
                  Create Project → Set up optimization scenarios
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1976d2', mr: 1, flexShrink: 0 }} />
                  Add Scenarios → Define "what-if" situations
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1976d2', mr: 1, flexShrink: 0 }} />
                  Create Datasets → Organize VRP data versions
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1976d2', mr: 1, flexShrink: 0 }} />
                  Import Data → Add locations, vehicles, jobs
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16a34a', mr: 1, flexShrink: 0 }} />
                  Run Optimization → Generate optimal routes
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - 1fr */}
        <Grid item xs={12} lg={4}>
          {/* Recent Activity Panel */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Recent Activity
              </Typography>
              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {activitiesLoading ? (
                  <Typography variant="body2" sx={{ color: '#999' }}>Loading activity...</Typography>
                ) : activities.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                    No recent activity. Start using the system to see activity here.
                  </Typography>
                ) : (
                  activities.map((activity, index) => (
                    <Box key={index} sx={{ mb: 2, p: 1, borderLeft: '3px solid #1976d2', pl: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{activity.title}</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>{activity.description}</Typography>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>{activity.time}</Typography>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>

          {/* System Status Panel */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  System Status
                </Typography>
                <Tooltip title={`Last updated: ${systemStatus.lastUpdate?.toLocaleTimeString() || 'Never'}`}>
                  <IconButton size="small" onClick={() => window.location.reload()}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2">Optimisation Engine</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(systemStatus.vroomSolver) }} />
                    <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {systemStatus.vroomSolver === 'checking' ? 'Checking...' : 
                       systemStatus.vroomSolver === 'online' ? 'Healthy' :
                       systemStatus.vroomSolver === 'warning' ? 'Degraded' : 
                       systemStatus.vroomSolver}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2">Database</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(systemStatus.database) }} />
                    <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {systemStatus.database === 'checking' ? 'Checking...' : 
                       systemStatus.database === 'online' ? 'Connected' :
                       systemStatus.database === 'warning' ? 'Limited' : 
                       systemStatus.database}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2">API Services</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(systemStatus.apiServices) }} />
                    <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {systemStatus.apiServices === 'checking' ? 'Checking...' : 
                       systemStatus.apiServices === 'online' ? 'Operational' :
                       systemStatus.apiServices === 'warning' ? 'Slow' : 
                       systemStatus.apiServices}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="body2">Background Jobs</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(systemStatus.backgroundJobs) }} />
                    <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                      {systemStatus.backgroundJobs === 'checking' ? 'Checking...' : 
                       systemStatus.backgroundJobs === 'online' ? 'Active' :
                       systemStatus.backgroundJobs === 'warning' ? 'Delayed' : 
                       systemStatus.backgroundJobs}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Performance Metrics */}
              {systemStatus.responseTime > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Response Time: {systemStatus.responseTime}ms | 
                    Last Check: {systemStatus.lastUpdate?.toLocaleTimeString() || 'Never'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </ProfessionalLayout>
  );
};